import React from 'react';
import style from './style';
import EAIcon from '../../../../../components/common/EAIcon';
import {
  Autocomplete,
  createFilterOptions,
  Grid,
  InputAdornment,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import i18next from 'i18next';
import moment from 'moment';
import { Controller, useFormContext } from 'react-hook-form';
import ExistingContext from '../ExistingContext';
import { defaultContextValues } from '../index';
import { DefaultSelectProps } from '../../../../../services/common/Constants';
import NewContext from '../NewContext';
import ExistingTP from '../ExistingTP';

const ModelSelection = ({ contextInstances, contextModels, isTpPresent, isCheckingPresent }) => {
  const { classes } = style();
  const { setValue, watch, control } = useFormContext();

  const existingContext = watch('context');
  const modelNewContext = watch('modelNewContext');

  const instanceFilter = createFilterOptions({
    stringify: (context) =>
      `${context.contextModelName} ${context.reference} ${
        context.creationDate
      } ${
        context.contextAttributes
          ?.flatMap((att) => att.values ?? [])
          .join(' ') ?? ''
      }`,
  });

  const onSelectModel = (e) => {
    const selectedModel = contextModels.find((c) => c.name === e.target.value);
    if (selectedModel) {
      setValue('modelNewContext', selectedModel);
      setValue('tp.tiersAttributes', selectedModel.tiersAttributes.map(t => ({
        ...t,
        attributeId: t.id,
        id: null,
      })));
      setValue('context', {
        ...defaultContextValues,
        contextModelName: selectedModel.name
      });
    } else {
      setValue('modelNewContext', defaultContextValues);
    }
  };

  const onSelectInstance = (e, context) => {
    if (context) {
      setValue('context', context);
      setValue('tp.tiersAttributes', context.tiersAttributes);
      setValue('modelNewContext', defaultContextValues);
    } else {
      setValue('context', defaultContextValues);
    }
  };

  return (
    <div className={classes.root}>
      <div className={classes.selectBlock}>
        <div className={classes.titleWrapper}>
          <EAIcon icon={'/context/addTiers2'} className={classes.icon} />
          <Typography variant={'h3'} className={classes.title}>
            {i18next.t('thirdpartySheet.contextTp.contextSelection')}
          </Typography>
        </div>
        <Grid container className={classes.selectContainer}>
          <Grid item xs={6}>
            <div className={classes.selectTitle}>
              {i18next.t('thirdpartySheet.contextTp.associateExistingContext')}
            </div>
            <div className={classes.selectSubTitle}>
              {i18next.t('thirdpartySheet.contextTp.contextSearchDescription')}
            </div>
            {contextInstances && (
              <Controller
                name="context"
                control={control}
                defaultValue={null}
                render={({ field }) => (
                  <Autocomplete
                    freeSolo
                    value={field.value ? field.value.name : ''}
                    className={classes.selectAutocomplete}
                    options={contextInstances.content}
                    filterOptions={instanceFilter}
                    getOptionLabel={(option) => {
                      const ref = option.reference || '';
                      return `${ref}`;
                    }}
                    onChange={onSelectInstance}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        placeholder={i18next.t(
                          'thirdpartySheet.contextTp.existingContextSelect'
                        )}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              <InputAdornment position="end">
                                <EAIcon icon={'context/tpSheet/search'} />
                              </InputAdornment>
                              {params.InputProps.startAdornment}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    renderOption={(props, option) => {
                      const name = option.contextModelName;
                      const ref = option.reference;
                      const date = option.creationDate
                        ? moment(option.creationDate).format('YYYY-MM-DD')
                        : '';
                      return (
                        <li {...props}>
                          <div className={classes.selectOption}>
                            <div className={classes.selectName}>{name}</div>
                            <div className={classes.selectRef}>{ref}</div>
                            <div className={classes.selectDate}>{date}</div>
                          </div>
                        </li>
                      );
                    }}
                    isOptionEqualToValue={(opt, val) => opt.id === val.id}
                  />
                )}
              />
            )}
          </Grid>
          <Grid item xs={1} className={classes.dividerContainer}>
            <div className={classes.divider}></div>
            <div className={classes.dividerText}>{i18next.t('OR')}</div>
            <div className={classes.divider}></div>
          </Grid>
          <Grid item xs={5}>
            <div className={classes.selectTitle}>
              {i18next.t('thirdpartySheet.contextTp.newContext')}
            </div>
            <div className={classes.selectSubTitle}>
              {i18next.t('thirdpartySheet.contextTp.contextModel')}
            </div>
            <Controller
              name="modelNewContext"
              control={control}
              defaultValue={null}
              render={({ field }) => (
                <TextField
                  id={`contextModelName`}
                  variant="outlined"
                  select
                  value={field.value ? field.value.name : ''}
                  className={classes.modelSelector}
                  onChange={onSelectModel}
                  SelectProps={{
                    ...DefaultSelectProps,
                    displayEmpty: true,
                    renderValue: (model) => {
                      if (model === "" || !model) {
                        return (
                          <span className={classes.placeholder}>
                            {i18next.t(
                              `thirdpartySheet.contextTp.selectContextPlaceholder`
                            )}
                          </span>
                        );
                      }
                      return <span>{model}</span>;
                    },
                  }}
                >
                  {contextModels.map((model) => (
                    <MenuItem key={model.name} value={model.name}>
                      {model.name}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Grid>
        </Grid>
        {existingContext.id && contextModels && !isTpPresent && !isCheckingPresent && (
          <ExistingContext
            contextInstance={existingContext}
          />
        )}
        {modelNewContext.id && !isTpPresent && !isCheckingPresent && <NewContext />}
        {isTpPresent && !isCheckingPresent && <ExistingTP isTpPresent={isTpPresent}/>}
      </div>
    </div>
  );
};

export default ModelSelection;
